import { Controller } from "@hotwired/stimulus"
import * as analytics from "../libs/analytics"

// Connects to data-controller="analytics"
//
// Initializes the analytics provider for client-side tracking.
// Only rendered for teachers/admins via the _analytics.html.erb partial.
// Consent handling depends on the market — some markets (e.g. UK) require
// explicit opt-in, while others (e.g. AU) may allow tracking without consent.
//
// Consent levels:
//   "all"            — analytics + session replay
//   "analytics_only" — analytics only, no replay
//   "rejected"       — nothing captured
export default class extends Controller {
  static values = {
    apiKey: String,
    apiHost: String,
    consent: String,
    userId: Number,
    userEmail: String,
    userRole: String,
    userCrt: Boolean,
    schoolId: Number,
    schoolName: String,
    market: String,
    subscriptionStatus: String,
    hasTeach: Boolean,
    hasInsights: Boolean,
    deviceFree: Boolean,
    authType: String,
    region: String,
    recordingsEnabled: Boolean,
    features: Object
  }

  connect() {
    if (!this.apiKeyValue) return

    analytics.init(this.apiKeyValue, { apiHost: this.apiHostValue, userId: this.userIdValue })
    this.#applyConsent()
  }

  disconnect() {
    // No-op: PostHog state persists in localStorage across Turbo navigations.
    // Calling reset() here would fragment sessions on every page change.
    // Identity is reset lazily in resetIfNewUser() when a different user logs in.
  }

  grantConsent() {
    this.#identifyUser()
    analytics.optIn()
    analytics.stopSessionRecording()
  }

  grantFullConsent() {
    this.#identifyUser()
    analytics.optIn()
    if (this.recordingsEnabledValue) {
      analytics.startSessionRecording()
    } else {
      analytics.stopSessionRecording()
    }
  }

  revokeConsent() {
    analytics.optOut()
    analytics.stopSessionRecording()
  }

  // Private

  #applyConsent() {
    if (this.consentValue === "all") {
      this.grantFullConsent()
    } else if (this.consentValue === "analytics_only" || this.consentValue === "accepted") {
      this.grantConsent()
    } else {
      this.revokeConsent()
    }
  }

  #identifyUser() {
    if (!this.userIdValue) return

    analytics.resetIfNewUser(this.userIdValue)
    analytics.identify(this.userIdValue, {
      email: this.userEmailValue,
      role: this.userRoleValue,
      crt: this.userCrtValue,
      school_id: this.schoolIdValue,
      market: this.marketValue,
      ...this.featuresValue
    })

    if (this.schoolIdValue) {
      analytics.group("school", this.schoolIdValue, {
        name: this.schoolNameValue,
        market: this.marketValue,
        subscription_status: this.subscriptionStatusValue,
        has_teach: this.hasTeachValue,
        has_insights: this.hasInsightsValue,
        device_free: this.deviceFreeValue,
        auth_type: this.authTypeValue,
        region: this.regionValue
      })
    }
  }
}
